import { Link as RouterLink } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import get from 'lodash/get'
import { useTeam } from '../../../../contexts/TeamContext'
import { useServices } from '../../../../contexts/ServicesContext'
import { useDeployments } from '../../../../contexts/DeploymentsContext'
import Flex from '../../../../shared/components/layouts/Flex'
import FlexColumn from '../../../../shared/components/layouts/FlexColumn'
import DeploymentStatusChip from '../../../../shared/components/DeploymentStatusChip'
import ProjectPendingChangesChip from '../../../../shared/components/ProjectPendingChangesChip'

const getServiceStatus = (service) => {
  if (service.currentStatuses && service.currentStatuses.length > 0) {
    return service.currentStatuses[0].state
  }
  return 'unknown'
}

const countServicesByStatus = (services) => {
  let running = 0
  let stopped = 0
  let other = 0

  services.forEach((service) => {
    const status = getServiceStatus(service)
    if (status === 'running') {
      running++
    } else if (status === 'stopped' || status === 'exited' || status === 'dead') {
      stopped++
    } else {
      other++
    }
  })

  return { running, stopped, other }
}

function DashboardProjectCard({ project }) {
  const { team } = useTeam()
  const { servicesByProjectId } = useServices()
  const { deploymentsByDeployableId } = useDeployments()

  const services = get(servicesByProjectId, project.id, [])
  const deployment = get(deploymentsByDeployableId, project.id)
  const { running, stopped, other } = countServicesByStatus(services)

  return (
    <Box
      component={RouterLink}
      to={`/teams/${team.slug}/docker/projects/${project.slug}`}
      sx={{
        display: 'block',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
          bgcolor: 'action.hover',
          textDecoration: 'none',
        },
      }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          {project.name}
        </Typography>
        <Flex gap={1} alignItems="center">
          {project.pendingChanges
            ? <ProjectPendingChangesChip project={project} />
            : <DeploymentStatusChip deployment={deployment} />}
        </Flex>
      </Flex>

      <FlexColumn gap={1} sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage
            id="dashboard.projects.servicesCount"
            values={{ count: services.length }}
          />
        </Typography>

        {services.length > 0 && (
          <Flex gap={1} sx={{ flexWrap: 'wrap' }}>
            {running > 0 && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={
                  <FormattedMessage
                    id="dashboard.projects.services.running"
                    values={{ count: running }}
                  />
                }
              />
            )}
            {stopped > 0 && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={
                  <FormattedMessage
                    id="dashboard.projects.services.stopped"
                    values={{ count: stopped }}
                  />
                }
              />
            )}
            {other > 0 && (
              <Chip
                size="small"
                color="default"
                variant="outlined"
                label={
                  <FormattedMessage
                    id="dashboard.projects.services.other"
                    values={{ count: other }}
                  />
                }
              />
            )}
          </Flex>
        )}
      </FlexColumn>
    </Box>
  )
}

export default DashboardProjectCard
